import React, { useEffect } from 'react';
import { GetServerSideProps } from 'next';

import AuthoredPageRoutes from '@/components/authored-page-routes/authored-page-routes';
import { PreRenderProps } from '@/pages/_app';
import { getViewDefinition } from '@/ssr/view-definition-page-query';
import { getSSRFeatureStatus, SSRPageIDs } from '@/utils';
import { ANALYTICS_EVENT_NAMES, collectMetrics, getFullPath, type IApiTiming } from '@iceberg/report-perfect-experience-analytics';
import { measureDuration, fetchDataAndHandleErrors, withTokenRefresh, applySSROptimizations } from '@/utils/ssr-utils';
import { BFF_PATH, cookies } from '@/data/constants';

export type PageProps = PreRenderProps & { ssrFetchData?: IApiTiming };

// Optimize SSR data fetching with caching headers
export const getServerSideProps: GetServerSideProps<PageProps> = withTokenRefresh(async (context: any) => {
  const { result: data, duration: ssrFetchDurationMs } = await measureDuration(() =>
    fetchDataAndHandleErrors(context, getViewDefinition)
  );

  const ssrFetchData: IApiTiming = {
    name: `POST ${BFF_PATH.DYNAMIC}`,
    duration: ssrFetchDurationMs,
  };

  let enableSSR = getSSRFeatureStatus(data?.kamFlags, SSRPageIDs.HOME_PAGE);

  if(context?.req?.cookies?.[cookies.ENABLE_FULL_SSR]) {
    enableSSR = context.req.cookies[cookies.ENABLE_FULL_SSR] === "true";
  }

  // Apply SSR optimizations only when SSR is enabled for this page
  if (enableSSR) {
    applySSROptimizations(data?.viewDefinitionResults);
  }

  // Do not change PageProps type dramatically (only add new props)
  // Some SDUI components (Masthead) reads the props manually via NextData in the DOM, not via component props.
  // Changing the type will break header rendering, specifically the bagCount and favourites.
  const props: PageProps & { enableSSR: boolean } = {
    ...data,
    enableSSR,
    ssrFetchData,
  };
  return { props };
});

const Home: React.FC<PageProps> = (props) => {
  const { ssrFetchData, ...data } = props;
  collectMetrics(ANALYTICS_EVENT_NAMES.PAGE_INIT, {
    detail: {
      page_name: getFullPath()
    }
  });

  useEffect(() => {
    collectMetrics(ANALYTICS_EVENT_NAMES.PAGE_READY, {
        detail: {
          page_name: getFullPath(),
          page_id: 'home'
        }
      }); 
  }, []);
  return (
    <AuthoredPageRoutes ssrFetchData={ssrFetchData} {...(data as any)} />
  );
};

export default Home;
